import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ArrowRight, Phone, MapPin } from "lucide-react";
import { COMPANY } from "@/i18n/translations";

const HERO_IMG = "/hero-lambo.webp";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskedLine({ children, i, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        custom={i}
        variants={lineReveal}
        initial="hidden"
        animate="visible"
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onExplore, onContact }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative h-screen w-full overflow-hidden flex items-end pb-24 md:pb-28 grain"
      data-testid="hero-section"
    >
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Lamborghini Huracán – DNA Automotive" className="w-full h-full object-cover" style={{ objectPosition: "center 35%" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-obsidian/30 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-brand-bright text-xs md:text-sm uppercase tracking-[0.3em] mb-5"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-heading font-black uppercase tracking-tighter text-white leading-[0.9] text-5xl sm:text-7xl lg:text-8xl xl:text-9xl">
          <MaskedLine i={0}>{t.hero.line1}</MaskedLine>
          <MaskedLine i={1} className="text-brand-bright">{t.hero.line2}</MaskedLine>
          <MaskedLine i={2}>{t.hero.line3}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="text-zinc-300 text-base md:text-lg max-w-xl mt-8 leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <button
            onClick={onExplore}
            data-testid="hero-cta-explore"
            className="group inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-7 py-4 hover:bg-brand-hover transition-colors"
          >
            {t.hero.cta1}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            href={`tel:${COMPANY.phoneHref}`}
            data-testid="hero-cta-call"
            className="inline-flex items-center gap-2 border border-white/25 text-white font-medium uppercase tracking-wider text-sm px-7 py-4 hover:border-brand-light hover:text-brand-bright transition-colors"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-10"
        >
          <span
            className="inline-flex items-center gap-2.5 bg-black/60 backdrop-blur-2xl border border-white/10 text-zinc-200 text-[11px] md:text-xs uppercase tracking-[0.25em] px-4 py-2.5"
            data-testid="motorworld-badge"
          >
            <MapPin className="w-3.5 h-3.5 text-brand-bright" />
            {COMPANY.location}
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
}
